import React, { useEffect, useState } from "react";
import { withRouter } from "../../../App";
import { connect, useDispatch } from "react-redux";
import { getLoggenInToken } from "../../../store/selectors/AuthSelectors";
import { Button, Modal, Spinner } from "react-bootstrap";
import { get } from "../../../services/api";
import Status from "../Common/Status";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { copyJobWithoutCandidates } from "../../../store/actions/Jobs/jobActions";
import { loginRequest } from "../../../authConfig";
import { useMsal } from "@azure/msal-react";

const CopyRequirement = ({
  showCopyModal,
  setShowCopyModal,
  selectedRequirement,
  token,
  candidates
}) => {
  const [requirements, setRequirements] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedRequirement?.jobID) {
      getRequirements();
      if (selectedRequirement.quantity) {
        setQuantity(selectedRequirement.quantity);
      }
    }
  }, [selectedRequirement]);

  const getRequirements = async () => {
    try {
      const res = await get(
        `/api/job/requirement/job/${selectedRequirement?.jobID}`,
        token
      );
      setRequirements(res);
    } catch (error) {
      console.error("Error fetching requirements:", error);
      toast.error("Failed to fetch requirements");
    }
  };

  // Modified to allow any date to be selected
  const getMinStartDate = () => {
    return null; // Return null to allow any date to be selected
  };

  const handleCopy = async () => {
    if (isLoading) return;
    
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      toast.error("End date must be after or the same as the start date.");
      return;
    }

    if (requirements.length === 0) {
      toast.error("No requirement found to copy");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { _id, _iv, ...jobData } = requirements[0];
      
      const copyPayload = {
        ...jobData,
        quantity: parseInt(quantity) || 1,
        jobStartDate: startDate,
        jobEndDate: endDate,
        candidates: 0
      };
      
      if (accounts?.length > 0) {
        try {
          const accessToken = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          copyPayload.accessToken = accessToken.accessToken;
        } catch (error) {
          console.error("Error getting access token:", error);
        }
      }
      
      const success = await dispatch(
        copyJobWithoutCandidates(
          copyPayload,
          selectedRequirement._id,
          handleModalClose,
          token
        )
      );
      
      if (success) {
        toast.success("Job copied successfully without candidates.");
      }
      
      if (!success) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error copying job:", error);
      toast.error("Failed to copy job: " + (error.message || "Unknown error"));
      setIsLoading(false);
    }
  };
  
  const handleModalClose = () => {
    setIsLoading(false);
    setShowCopyModal(false);
  };

  return (
    <Modal
      className="fade bd-example-modal-lg"
      show={showCopyModal}
      size="lg"
      onHide={() => !isLoading && handleModalClose()}
    >
      <Modal.Header>
        <Modal.Title>
          Copy Requirement ({selectedRequirement?.jobID})
        </Modal.Title>
        <Button
          variant=""
          className="btn-close"
          onClick={() => !isLoading && handleModalClose()}
          disabled={isLoading}
        ></Button>
      </Modal.Header>
      <Modal.Body>
        <h3>Available Requirements with Same Plan ID</h3>
        <div id="user-tbl_wrapper" className="dataTables_wrapper no-footer" style={{overflowX: "auto"}}>
          <table
            id="projects-tbl"
            className="table ItemsCheckboxSec dataTable no-footer mb-2 mb-sm-0 scrollable-content"
          >
            <thead>
              <tr>
                <th>Plan ID</th>
                <th>Company</th>
                <th>Job Title</th>
                <th>Quantity</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {requirements.map((item, index) => (
                <tr key={index}>
                  <td>
                    <div className="d-flex align-items-center">
                      <p className="mb-0 ms-2">{item?.jobID || "--"}</p>
                    </div>
                  </td>
                  <td>{item?.company}</td>
                  <td>{item?.jobTitle}</td>
                  <td>{item?.quantity}</td>
                  <td>{item.jobStartDate ? dayjs(item.jobStartDate).format("DD-MM-YYYY") : "--"}</td>
                  <td>{item.jobEndDate ? dayjs(item.jobEndDate).format("DD-MM-YYYY") : "--"}</td>
                  <td>
                    <Status status={item.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-4">Copy Requirement</h3>
        <div className="d-flex gap-3">
          <div>
            <label>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={getMinStartDate()}
              disabled={isLoading}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || getMinStartDate()}
              disabled={isLoading}
              placeholder="Select end date"
            />
          </div>
          <div>
            <label>Quantity</label>
            <input
              type="number"
              className="form-control"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              disabled={isLoading}
            />
          </div>
        </div>
        
        <div className="mt-4">
          <div className="alert alert-info">
            <p className="mb-0">
              <i className="fa fa-info-circle me-2"></i>
              This cannot copy the candidates.
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => !isLoading && handleModalClose()} disabled={isLoading}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCopy} 
          disabled={isLoading}
          style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
        >
          {isLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Processing...</span>
            </>
          ) : (
            "Copy"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    token: getLoggenInToken(state),
    candidates: state.candidates.candidates || [],
  };
};

export default withRouter(connect(mapStateToProps)(CopyRequirement));
